import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import {
  CitationSourceLink,
  EmptyState,
  ErrorState,
  KpiHeroBand,
  KpiBandSkeleton,
  TableSkeleton,
  WorkflowPipeline,
  buildPipelineSteps,
  PageHeader,
  StatusChip,
  toast,
  activityRail,
} from '../../components/index.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import './briefingDetail.css';
import './briefings.redesign.css';

const LIFECYCLE_ORDER = ['draft', 'pending_review', 'approved_for_export', 'exported'];
const LIFECYCLE_LABELS = {
  draft: 'Draft',
  pending_review: 'In review',
  approved_for_export: 'Approved',
  exported: 'Exported',
};

function normalizeStatus(status) {
  const lower = String(status || 'draft').toLowerCase();
  if (lower === 'approved' || lower === 'humanreviewed' || lower === 'human_reviewed') {
    return 'approved_for_export';
  }
  if (lower === 'pendingreview') return 'pending_review';
  if (lower === 'generated') return 'approved_for_export';
  return lower;
}

function pipelineForProduct(status) {
  const normalized = normalizeStatus(status);
  if (normalized === 'needs_rework') {
    return buildPipelineSteps({
      order: LIFECYCLE_ORDER,
      current: 'pending_review',
      statusByKey: { draft: 'done', pending_review: 'blocked' },
      labels: LIFECYCLE_LABELS,
    });
  }
  return buildPipelineSteps({
    order: LIFECYCLE_ORDER,
    current: normalized,
    labels: LIFECYCLE_LABELS,
  });
}

// Audit-event tone: approvals/exports clear sage, rework/denials brick, review
// touches amber, everything else neutral blue.
function auditTone(action) {
  const value = String(action || '').toLowerCase();
  if (value.includes('approve') || value.includes('export') || value.includes('complete')) {
    return 'sage';
  }
  if (value.includes('rework') || value.includes('reject') || value.includes('den')) {
    return 'brick';
  }
  if (value.includes('review') || value.includes('submit')) return 'amber';
  return 'blue';
}

function fmtDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function prettyScopeType(value) {
  return String(value || 'briefing')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toUpperCase();
}

function unwrapEnvelope(payload) {
  return payload?.data?.data || payload?.data || payload;
}

function pathFromDownloadUrl(downloadUrl) {
  if (!downloadUrl) return null;
  try {
    return new URL(downloadUrl).toString();
  } catch {
    const apiBase = import.meta.env?.VITE_API_BASE || '/api';
    const originBase = typeof window !== 'undefined' ? window.location.origin : 'http://127.0.0.1';
    const downloadPath = downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`;

    if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
      return new URL(downloadPath, apiBase.replace(/\/api\/?$/, '/')).toString();
    }

    return new URL(downloadPath, originBase).toString();
  }
}

const SUPPORTED_EXPORT_FORMATS = new Set(['pptx', 'pdf', 'docx', 'html']);

function exportFormatsForProduct(product = {}) {
  const formats = Array.isArray(product?.exportFormats) ? product.exportFormats : [];
  const normalized = formats
    .map((format) => String(format || '').toLowerCase())
    .filter((format) => SUPPORTED_EXPORT_FORMATS.has(format));
  return normalized.length ? normalized : ['pptx', 'pdf'];
}

function fallbackMessage(record = {}) {
  if (record.fallback?.occurred !== true) return null;
  const requested = String(record.requestedFormat || 'requested format').toUpperCase();
  const actual = String(record.actualFormat || record.format || 'html').toUpperCase();
  const reason =
    {
      renderer_unavailable: 'PDF renderer unavailable',
      renderer_launch_failed: 'PDF renderer could not start',
      render_failed: 'PDF rendering failed',
      timeout: 'PDF rendering timed out',
      file_write_failed: 'PDF file could not be written',
      empty_output: 'PDF renderer produced an empty file',
      partial_output: 'PDF renderer produced an incomplete file',
      structural_validation_failed: 'PDF output failed validation',
      legacy_format_mismatch: 'legacy metadata did not match the stored file',
    }[record.fallback.reason] || 'requested format was unavailable';
  return `Delivered as ${actual} — ${requested} requested; ${reason}.`;
}

const TABS = [
  { id: 'preview', label: 'Preview' },
  { id: 'source_evidence', label: 'Source evidence' },
  { id: 'ai_insights', label: 'AI insights' },
  { id: 'review_comments', label: 'Review comments' },
  { id: 'versions', label: 'Versions' },
  { id: 'export_history', label: 'Export history' },
  { id: 'audit_trail', label: 'Audit trail' },
];

// Confirm copy per review action (AC-PROD-2.1). The reviewer types the note —
// no boilerplate autofill.
const REVIEW_ACTION_META = {
  approve_for_export: {
    title: 'Approve this product for export?',
    body: 'Approving records you as the named human reviewer and unlocks the export controls for this product.',
    confirmLabel: 'Approve for export',
    tone: 'primary',
    noteLabel: 'Reviewer note',
    notePlaceholder: 'What did you verify before approving?',
  },
  needs_rework: {
    title: 'Send this product back for rework?',
    body: 'The product returns to its author with your feedback and stays blocked from export until it is re-approved.',
    confirmLabel: 'Send for rework',
    tone: 'danger',
    noteLabel: 'Rework feedback',
    notePlaceholder: 'Tell the author what must change.',
  },
};

export function BriefingDetailPage() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('preview');
  // Review action awaiting confirm + typed note (AC-PROD-2.1).
  const [pendingAction, setPendingAction] = useState(null);
  const tabRefs = useRef({});
  const productRes = useApi(`/briefing-products/${id}`);
  const capabilitiesRes = useApi('/briefing-export-capabilities');
  const reviewMut = useMutation();
  const exportMut = useMutation();

  const product = productRes.data?.data || null;
  // AC-W7 — factory-generated products carry the originating run under sourceAiRunId; escalated
  // drafts under relatedAIRunId/aiRunId. Any of them drives the "View governed run" cross-link.
  const aiRunId = product?.aiRunId || product?.relatedAIRunId || product?.sourceAiRunId || null;
  const aiRes = useApi(aiRunId ? `/ai/runs/${aiRunId}` : null, { skip: !aiRunId });
  const aiRun = aiRes.data?.data || null;

  const citations = product?.citations || aiRun?.citations || [];
  const sections = Array.isArray(product?.sections) ? product.sections : [];
  const versions = product?.versions || [];
  const exportHistory = product?.exportHistory || [];
  const reviewComments = product?.reviewComments || [];
  const auditTrail = product?.auditTrail || [];
  const aiInsights = product?.aiInsights || [];
  const uniqueSourceArtifacts = (product?.sourceArtifactIds || []).filter(
    (artifactId) => !citations.some((citation) => citation.source === artifactId),
  );
  const openHref = pathFromDownloadUrl(exportHistory[exportHistory.length - 1]?.downloadUrl);
  const pdfCapability = capabilitiesRes.data?.data?.pdf || null;
  const pdfUnavailable = capabilitiesRes.error || pdfCapability?.available === false;
  const exportFormats = exportFormatsForProduct(product).filter(
    (format) => format !== 'pdf' || !pdfUnavailable,
  );

  usePageMetadata({
    title: product?.title || `Briefing ${id || ''}`.trim(),
    subtitle: product
      ? `Generated ${fmtDate(product.generatedAt || product.createdAt)} · v${product.version || 1}`
      : 'Preview, citations, AI insights, and export posture for governed briefing products.',
  });

  async function reviewProduct(action, note) {
    try {
      await reviewMut.run(() =>
        api.post(`/briefing-products/${product.id}/review`, {
          action,
          note,
        }),
      );
      setPendingAction(null);
      toast.success({
        title: action === 'approve_for_export' ? 'Product approved' : 'Product sent for rework',
        body: product.title,
      });
      activityRail.push({
        action: `briefing_product.${action}`,
        targetType: 'briefing_product',
        targetId: product.id,
        actorUserId: 'you',
      });
      await productRes.refresh?.();
    } catch (error) {
      toast.error({ title: 'Review failed', body: error.message });
    }
  }

  async function exportProduct(format) {
    try {
      const response = await exportMut.run(() =>
        api.post(`/briefing-products/${product.id}/export`, { format }),
      );
      const payload = unwrapEnvelope(response);
      const exportRecord = payload.exportRecord || {};
      const actualFormat = exportRecord.actualFormat || exportRecord.format || format;
      toast.success({
        title: `${String(actualFormat).toUpperCase()} export created`,
        body: fallbackMessage(exportRecord) || exportRecord.fileName || product.title,
      });
      activityRail.push({
        action: 'briefing_product.export',
        targetType: 'briefing_product',
        targetId: product.id,
        actorUserId: 'you',
        summary: format,
      });
      await productRes.refresh?.();
    } catch (error) {
      toast.error({ title: 'Export failed', body: error.message });
    }
  }

  if (productRes.loading) {
    return (
      <div className="briefings-redesign">
        <PageHeader title={`Briefing ${id}`} />
        <KpiBandSkeleton count={4} />
        <div style={{ marginTop: 'var(--space-4)' }}>
          <TableSkeleton rows={6} />
        </div>
      </div>
    );
  }
  if (productRes.error) return <ErrorState error={productRes.error} />;
  if (!product) return <EmptyState title="Briefing not found" />;

  const approvedForExport = product.status === 'approved_for_export';
  // G7-APP-002: an exported product sits PAST the approval gate — exports stay
  // available for its other formats and the approval action stays settled.
  const exportEligible = approvedForExport || product.status === 'exported';

  const sourceCount = product.citationCount || citations.length || 0;
  const statusNormalized = normalizeStatus(product.status);
  const statusBlocked = statusNormalized === 'needs_rework';
  const kpiItems = [
    {
      id: 'version',
      label: 'Version',
      value: `v${product.version || 1}`,
      status: 'blue',
      footer: 'Retained product version',
    },
    {
      id: 'sources',
      label: 'Source citations',
      value: sourceCount,
      status: sourceCount ? 'sage' : 'amber',
      statusLabel: sourceCount ? 'Grounded' : 'Unsourced',
      footer: `${uniqueSourceArtifacts.length} attached artifact${uniqueSourceArtifacts.length === 1 ? '' : 's'}`,
    },
    {
      id: 'exports',
      label: 'Exports',
      value: exportHistory.length,
      status: exportHistory.length ? 'sage' : 'slate',
      statusLabel: exportHistory.length ? 'Delivered' : 'None',
      footer: `${exportFormats.length} format${exportFormats.length === 1 ? '' : 's'} enabled`,
    },
    {
      id: 'governance',
      label: 'Review posture',
      value: exportEligible ? 'Approved' : statusBlocked ? 'Rework' : 'In review',
      status: exportEligible ? 'sage' : statusBlocked ? 'brick' : 'amber',
      statusLabel: `${auditTrail.length} event${auditTrail.length === 1 ? '' : 's'}`,
      footer: `${reviewComments.length} review note${reviewComments.length === 1 ? '' : 's'}`,
    },
  ];

  return (
    <div className="briefings-redesign">
      <nav className="briefing-detail-crumbs" aria-label="Breadcrumb">
        <Link to="/products">Briefing Product Factory</Link>
        <span className="sep">›</span>
        <span>{product.title || product.id}</span>
      </nav>
      <PageHeader
        eyebrow={`PRODUCT · ${prettyScopeType(product.scopeType)} · ${product.scopeId || product.id}`}
        title={product.title || product.id}
        subtitle={`Generated ${fmtDate(product.generatedAt || product.createdAt)} · v${product.version || 1}`}
        actions={<StatusChip status={product.status || 'draft'} />}
      />

      <KpiHeroBand eyebrow="Product posture" items={kpiItems} columns={4} />

      <div className="briefing-status-rail">
        <WorkflowPipeline
          steps={pipelineForProduct(product.status)}
          ariaLabel="Briefing product lifecycle status"
        />
      </div>

      <div className="briefing-detail-actions">
        <button
          className="btn success sm tactile-press"
          onClick={() => setPendingAction('approve_for_export')}
          disabled={reviewMut.running || exportEligible}
        >
          {exportEligible ? 'Approved for export' : 'Approve for export'}
        </button>
        <button
          className="btn outline sm"
          onClick={() => setPendingAction('needs_rework')}
          disabled={reviewMut.running || statusBlocked}
          title={statusBlocked ? 'This product is already in rework.' : undefined}
        >
          Needs rework
        </button>
        {exportFormats.map((format) => (
          <button
            key={format}
            className="btn outline sm"
            onClick={() => exportProduct(format)}
            disabled={exportMut.running || !exportEligible}
            title={
              exportEligible
                ? undefined
                : 'Export unlocks after a named human approves this product for export.'
            }
          >
            {`Export ${format.toUpperCase()}`}
          </button>
        ))}
        {openHref ? (
          <a className="btn link sm" href={openHref} target="_blank" rel="noopener noreferrer">
            Open generated file ↗
          </a>
        ) : null}
      </div>
      {pdfUnavailable ? (
        <p className="muted small" role="status" style={{ marginTop: 'var(--space-1)' }}>
          PDF unavailable in this environment — exports will be HTML.
        </p>
      ) : null}
      {!exportEligible && exportFormats.length ? (
        // AC-PROD-2.1: the gate explains itself instead of leaving dead controls.
        <p className="muted small" style={{ marginTop: 'var(--space-1)' }}>
          Exports stay locked until a named human reviewer approves this product — AI-generated
          drafts never leave PRIME without that approval.
        </p>
      ) : null}

      {/* AC-PROD-2.3: real tabs — role=tab, aria-selected, arrow-key navigation
          with a roving tabindex. */}
      <div
        className="briefing-tab-row"
        role="tablist"
        aria-label="Briefing product detail tabs"
        onKeyDown={(event) => {
          const currentIndex = TABS.findIndex((tab) => tab.id === activeTab);
          let nextIndex = null;
          if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TABS.length;
          else if (event.key === 'ArrowLeft')
            nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = TABS.length - 1;
          if (nextIndex == null) return;
          event.preventDefault();
          const nextTab = TABS[nextIndex];
          setActiveTab(nextTab.id);
          tabRefs.current[nextTab.id]?.focus();
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            ref={(node) => {
              tabRefs.current[tab.id] = node;
            }}
            className={`briefing-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            role="tab"
            id={`briefing-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`briefing-tabpanel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'preview' ? (
        <section
          className="bento briefing-bento"
          role="tabpanel"
          id="briefing-tabpanel-preview"
          aria-labelledby="briefing-tab-preview"
          style={{ marginTop: 'var(--space-3)' }}
        >
          <div className="span-8 card">
            <div className="eyebrow">Executive summary</div>
            <h2 className="card-title">Preview</h2>
            <p className="briefing-prose">
              {product.summary || aiRun?.summary || 'No executive summary has been generated yet.'}
            </p>
          </div>
          <div className="span-4 card">
            <div className="eyebrow">Controls</div>
            <h2 className="card-title">Product posture</h2>
            <dl className="briefing-provenance">
              <div>
                <dt>Product type</dt>
                <dd>{product.productType || '—'}</dd>
              </div>
              <div>
                <dt>Template</dt>
                <dd>{product.templateName || product.templateId || '—'}</dd>
              </div>
              <div>
                <dt>Generated by</dt>
                <dd>{product.generatedByUserId || product.createdByUserId || '—'}</dd>
              </div>
              <div>
                <dt>Reviewer</dt>
                <dd>{product.reviewerUserId || '—'}</dd>
              </div>
            </dl>
          </div>
          <div className="span-12 card">
            <div className="eyebrow">Sections</div>
            <h2 className="card-title">Generated content</h2>
            {sections.length === 0 ? (
              <EmptyState
                title="No sections generated"
                description="Generate or edit the product to populate section content."
              />
            ) : (
              <div className="briefing-section-stack">
                {sections.map((section) => (
                  <article key={section.id} className="briefing-section-card">
                    <strong>{section.id.replace(/_/g, ' ')}</strong>
                    <p className="briefing-prose">{section.body || 'No content yet.'}</p>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      ) : null}

      {activeTab === 'source_evidence' ? (
        <section
          className="bento briefing-bento"
          role="tabpanel"
          id="briefing-tabpanel-source_evidence"
          aria-labelledby="briefing-tab-source_evidence"
          style={{ marginTop: 'var(--space-3)' }}
        >
          <div className="span-5 card">
            <div className="eyebrow">Source evidence</div>
            <h2 className="card-title">{product.citationCount || citations.length || 0} sources</h2>
            {citations.length === 0 ? (
              <EmptyState
                title="No citations attached"
                description="Citations will appear once the product is grounded in source evidence."
              />
            ) : (
              <ol className="briefing-citations">
                {citations.map((citation, index) => (
                  <li key={`${citation.source || 'citation'}-${index}`}>
                    {/* Every citation navigates to its record view or explains why it
                        can't (AC-TRUST-3); never a bare count or dead label. */}
                    <strong>
                      <CitationSourceLink id={citation.source || `Source ${index + 1}`} />
                    </strong>
                    {citation.excerpt ? <div className="excerpt">{citation.excerpt}</div> : null}
                  </li>
                ))}
              </ol>
            )}
          </div>
          <div className="span-7 card">
            <div className="eyebrow">Artifacts</div>
            <h2 className="card-title">Attached source records</h2>
            {uniqueSourceArtifacts.length ? (
              <div className="briefing-artifact-chip-row">
                {uniqueSourceArtifacts.map((artifactId) => (
                  <CitationSourceLink
                    key={artifactId}
                    id={artifactId}
                    className="chip outline sm mono"
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                title="No source artifacts"
                description="Attach source records to strengthen export traceability."
              />
            )}
          </div>
        </section>
      ) : null}

      {activeTab === 'ai_insights' ? (
        <section
          className="card briefing-tab-card"
          role="tabpanel"
          id="briefing-tabpanel-ai_insights"
          aria-labelledby="briefing-tab-ai_insights"
        >
          <div className="eyebrow">AI insights</div>
          <h2 className="card-title">Governed AI contribution</h2>
          {aiInsights.length ? (
            <ul className="briefing-list">
              {aiInsights.map((insight) => (
                <li key={insight}>{insight}</li>
              ))}
            </ul>
          ) : (
            <p className="briefing-prose">
              {aiRun?.summary || 'No AI insights were attached to this product.'}
            </p>
          )}
          {aiRunId ? (
            // AC-PROD-2.2 / AC-AI-3: the AI-insights tab links the generating run dossier.
            <p className="briefing-prose">
              <Link className="btn link sm" to={`/ai/runs/${aiRunId}`}>
                View governed run {aiRunId}
              </Link>
            </p>
          ) : null}
        </section>
      ) : null}

      {activeTab === 'review_comments' ? (
        <section
          className="card briefing-tab-card"
          role="tabpanel"
          id="briefing-tabpanel-review_comments"
          aria-labelledby="briefing-tab-review_comments"
        >
          <div className="eyebrow">Review comments</div>
          <h2 className="card-title">Human review log</h2>
          {reviewComments.length ? (
            <ul className="briefing-list">
              {reviewComments.map((comment) => (
                <li key={comment.id || comment.note}>
                  <strong>
                    <PersonLabel userId={comment.actorUserId} fallback="Reviewer" showId />
                  </strong>
                  <p className="briefing-prose">{comment.note || 'No note recorded.'}</p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No review comments"
              description="Reviewer notes will appear here when the product is approved or sent back for rework."
            />
          )}
        </section>
      ) : null}

      {activeTab === 'versions' ? (
        <section
          className="card briefing-tab-card"
          role="tabpanel"
          id="briefing-tabpanel-versions"
          aria-labelledby="briefing-tab-versions"
        >
          <div className="eyebrow">Versions</div>
          <h2 className="card-title">Retained product versions</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((version) => (
                <tr key={version.id}>
                  <td>v{version.version || 1}</td>
                  <td>
                    <StatusChip status={version.status} />
                  </td>
                  <td>{fmtDate(version.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {activeTab === 'export_history' ? (
        <section
          className="card briefing-tab-card"
          role="tabpanel"
          id="briefing-tabpanel-export_history"
          aria-labelledby="briefing-tab-export_history"
        >
          <div className="eyebrow">Export history</div>
          <h2 className="card-title">Generated files</h2>
          {exportHistory.length ? (
            <table className="table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Format</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Generated</th>
                  <th>Download</th>
                </tr>
              </thead>
              <tbody>
                {exportHistory.map((record) => (
                  <tr key={record.id}>
                    <td>{record.fileName}</td>
                    <td>
                      {String(record.actualFormat || record.format || '').toUpperCase()}
                      {fallbackMessage(record) ? (
                        <div className="muted small">{fallbackMessage(record)}</div>
                      ) : null}
                    </td>
                    <td>v{record.version || 1}</td>
                    <td>
                      <StatusChip status={record.status} />
                    </td>
                    {/* AC-PROD-1.2: export rows carry timestamps + download links. */}
                    <td>{fmtDate(record.generatedAt)}</td>
                    <td>
                      {record.downloadUrl ? (
                        <a
                          className="btn link sm"
                          href={pathFromDownloadUrl(record.downloadUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download ↗
                        </a>
                      ) : (
                        <span className="muted small">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState
              title="No exports yet"
              description="Approved products will record PPTX, PDF, or DOCX exports here."
            />
          )}
        </section>
      ) : null}

      {activeTab === 'audit_trail' ? (
        <section
          className="card briefing-tab-card"
          role="tabpanel"
          id="briefing-tabpanel-audit_trail"
          aria-labelledby="briefing-tab-audit_trail"
        >
          <div className="eyebrow">Audit trail</div>
          <h2 className="card-title">Governed activity</h2>
          {auditTrail.length ? (
            <ol className="briefing-audit-timeline">
              {auditTrail.map((entry) => {
                const action = entry.action || 'audit_event';
                return (
                  <li
                    key={entry.id || `${action}-${entry.occurredAt || ''}`}
                    className={`briefing-audit-item tone-${auditTone(action)}`}
                  >
                    <span className="briefing-audit-item__action">{action}</span>
                    <p className="briefing-audit-item__time">
                      {fmtDate(entry.occurredAt || entry.createdAt)}
                    </p>
                    {entry.actorUserId ? (
                      <span className="briefing-audit-item__actor">
                        <PersonLabel userId={entry.actorUserId} showId />
                      </span>
                    ) : null}
                    {entry.summary ? (
                      <p className="briefing-audit-item__summary">{entry.summary}</p>
                    ) : /approve_for_export|needs_rework/.test(action) ? (
                      // G7-APP-003: a legacy review event without a stored
                      // rationale says so — silence must never look like intent.
                      <p className="briefing-audit-item__summary muted">No rationale recorded.</p>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          ) : (
            <EmptyState
              title="No audit entries attached"
              description="Generation, review, and export events will appear here as the product advances."
            />
          )}
        </section>
      ) : null}

      {/* AC-PROD-2.1: review decisions confirm with a typed note from the
          named human — the export gate is never opened silently. */}
      <ConfirmActionDialog
        open={Boolean(pendingAction)}
        title={REVIEW_ACTION_META[pendingAction]?.title || 'Confirm review decision'}
        body={REVIEW_ACTION_META[pendingAction]?.body || ''}
        confirmLabel={REVIEW_ACTION_META[pendingAction]?.confirmLabel || 'Confirm'}
        tone={REVIEW_ACTION_META[pendingAction]?.tone || 'primary'}
        requireNote
        noteLabel={REVIEW_ACTION_META[pendingAction]?.noteLabel || 'Reviewer note'}
        notePlaceholder={REVIEW_ACTION_META[pendingAction]?.notePlaceholder}
        busy={reviewMut.running}
        onConfirm={(note) => pendingAction && reviewProduct(pendingAction, note)}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}
